/**
 * This is the entry point for Feature Hub App integration
 */

import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import { ReactFeatureApp } from '@feature-hub/react';
import { AuthServiceProviderV4, AuthServiceV4 } from '@oneaudi/audi-auth-service';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { ContentService, ContentServiceV1 } from '@oneaudi/content-service';
import { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import { AudiFootnoteServiceInterfaceV1 } from '@oneaudi/footnote-service';
import { I18NServiceV1 } from '@oneaudi/i18n-service';
import { NumberFormatterServiceV1 } from '@oneaudi/number-formatter-service/dist/v1';
import { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import { EcommerceServiceV1 } from '@volkswagen-onehub/e-commerce-service';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { AsyncStateHolder, createInitialState } from '~/app/contexts/reducers/app-reducer';
import { AEM_MARKET_CONFIG, type AemMarketConfig, FalconFields } from '~/app/market-config';
import defaultContent from '~/manifest/content/editor.json';
import { mapFalconToAemMarketConfig } from '~/utils/mapFalconToAemMarketConfig';
import FeatureApp from './FeatureApp';

export interface FeatureServiceDependencies extends FeatureServices {
	readonly 's2:logger'?: Logger;
	readonly 'gfa:locale-service': GfaLocaleServiceV1;
	readonly 'dbad:audi-i18n-service': I18NServiceV1;
	readonly 'audi-content-service': ContentServiceV1;
	readonly 'audi-footnote-reference-service'?: AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
	readonly 'audi-footnote-service'?: AudiFootnoteServiceInterfaceV1;
	readonly 'audi-tracking-service'?: TrackingServiceV2;
	readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
	readonly 'audi-number-formatter-service': NumberFormatterServiceV1;
	readonly 'onegraph-service': OneGraphServiceV1;
	readonly 'e-commerce-service': EcommerceServiceV1;
	readonly 'vw:authService': AuthServiceProviderV4;
}

export const FEATURE_APP_NAME = 'fa-ngc-one-pager';

export interface AppConfig {
	__type?: 'json';
	content: AemMarketConfig;
	isStage: boolean;
}

export interface FalconConfig {
	__type?: 'aem-headless';
	fields: FalconFields;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isFalconConfig(obj: any): obj is FalconConfig {
	return obj && obj.__type;
}

// (!) Add this mapping function
export const normalizeConfig = (config: AemMarketConfig | FalconConfig): AemMarketConfig => {
	if (isFalconConfig(config)) {
		if (config.fields) {
			return mapFalconToAemMarketConfig(config.fields);
		}
		return defaultContent as AemMarketConfig;
	}
	return config;
};

const featureAppDefinition: FeatureAppDefinition<
	ReactFeatureApp,
	FeatureServiceDependencies,
	AppConfig | FalconConfig
> = {
	dependencies: {
		featureServices: {
			'gfa:locale-service': '^1.0.0',
			'dbad:audi-i18n-service': '^1.0.0',
			'audi-content-service': '^1.0.0',
			'layer-manager': '^2.4.0',
			'audi-number-formatter-service': '^1.0.0',
			'onegraph-service': '^1.0.0',
			'vw:authService': '^4.0.0',
		},
		externals: {
			react: '*',
			'styled-components': '*',
			'@volkswagen-onehub/onegraph-client': '*',
		},
	},
	optionalDependencies: {
		featureServices: {
			'audi-tracking-service': '^2.0.0',
			'audi-footnote-reference-service': '^3.0.0',
			'audi-footnote-service': '^1.1.0',
			's2:async-ssr-manager': '^1.0.0',
			's2:logger': '^1.0.0',
			'navigation-service': '^1.0.0',
			'gfa:navigation-service': '^1.0.0',
			'e-commerce-service': '^1.0.0',
		},
	},

	create: ({
		featureServices,
		config,
		featureAppId,
	}: FeatureAppEnvironment<FeatureServiceDependencies, AppConfig>) => {
		const loggerService = featureServices['s2:logger'];
		const i18nService = featureServices['dbad:audi-i18n-service'];
		const localeService = featureServices['gfa:locale-service'];
		const contentService = featureServices['audi-content-service'] as ContentService;
		const footnoteReferenceService = featureServices['audi-footnote-reference-service'];
		const footnoteService = featureServices['audi-footnote-service'];
		const trackingService = featureServices['audi-tracking-service'];
		const numberFormatterService = featureServices['audi-number-formatter-service'];
		const oneGraphClient = featureServices['onegraph-service'].getApolloClient();
		const eCommerceService = featureServices['e-commerce-service'];
		const provider = featureServices['vw:authService'];
		const authService: AuthServiceV4 = provider.register('myaudi');

		if (trackingService) {
			trackingService.featureAppName = featureAppId;
		}

		const getAemConfig = (_config: AemMarketConfig) => {
			if (!_config) return;
			const {
				market,
				language,
				carlineId,
				hideStandardAccessories,
				stockCarConfig,
				enableDynamicSummaryFooterButton,
				nbas,
				additionalPrimaryNba,
				combinations,
				summaryFooterCta,
				secondarySummaryCta,
				disablePdfNba,
				omnibus,
				deliveryCostUrl,
				showAccessoriesInclusive,
				showAccessoriesExclusive,
				engineCardKeyfacts,
				showCaESummaryText,
				enableFinancingServices,
				enablePartsRateCalculation,
				hideMonthlyRates,
				enableIncentives,
				specialOfferUrl,
				useIframe,
				enableEngineFilter,
				enable3d,
				globalDisclaimer,
				categoryDisclaimers,
				leanCheckoutEnabled,
			} = _config;

			const aemConfig: AemMarketConfig = {
				market: market ?? 'de',
				language: language ?? 'de',
				carlineId: carlineId ?? 'q4etron',
				hideStandardAccessories: hideStandardAccessories ?? undefined,
				enableDynamicSummaryFooterButton: enableDynamicSummaryFooterButton ?? undefined,
				disablePdfNba: disablePdfNba ?? false,
				stockCarConfig: stockCarConfig ?? undefined,
				combinations: combinations ?? undefined,
				nbas: nbas ?? undefined,
				additionalPrimaryNba: additionalPrimaryNba ?? undefined,
				summaryFooterCta: summaryFooterCta ?? undefined,
				secondarySummaryCta: secondarySummaryCta ?? undefined,
				omnibus: omnibus ?? undefined,
				deliveryCostUrl: deliveryCostUrl ?? undefined,
				showAccessoriesInclusive: showAccessoriesInclusive ?? undefined,
				showAccessoriesExclusive: showAccessoriesExclusive ?? undefined,
				engineCardKeyfacts: engineCardKeyfacts ?? undefined,
				showCaESummaryText: showCaESummaryText ?? undefined,
				enableFinancingServices: enableFinancingServices ?? undefined,
				hideMonthlyRates: enableFinancingServices ? hideMonthlyRates : enableFinancingServices || undefined,
				enablePartsRateCalculation: enablePartsRateCalculation ?? undefined,
				enableIncentives: enableIncentives ?? undefined,
				specialOfferUrl: specialOfferUrl ?? undefined,
				useIframe: useIframe ?? undefined,
				enableEngineFilter: enableEngineFilter ?? true,
				enable3d: enable3d ?? undefined,
				globalDisclaimer: globalDisclaimer ?? undefined,
				categoryDisclaimers: categoryDisclaimers ?? undefined,
				leanCheckoutEnabled: leanCheckoutEnabled ?? undefined,
			};

			Object.assign(AEM_MARKET_CONFIG, aemConfig);
			return aemConfig;
		};

		const content: AemMarketConfig =
			getAemConfig(normalizeConfig(config?.content || contentService.getContent() || defaultContent)) ||
			({} as AemMarketConfig);

		// SSR
		// --------------------------------------------------------------------
		const asyncSSRManager = featureServices['s2:async-ssr-manager'];
		// on the server
		let asyncStateHolder: AsyncStateHolder;

		if (asyncSSRManager) {
			asyncSSRManager.scheduleRerender(
				(async () => {
					asyncStateHolder = await createInitialState(
						localeService,
						i18nService,
						numberFormatterService,
						content,
						config?.isStage || false,
						authService,
					);
				})(),
			);
		} else {
			loggerService?.warn(
				`Serialized state not found!. Possible reasons: \n
    1. Running app in dev mode using the "audi-static-demo-integrator" which does not support SSR \n
    2. Running app in prod mode and SSR is broken`,
			);
			asyncStateHolder = () =>
				createInitialState(
					localeService,
					i18nService,
					numberFormatterService,
					content,
					config?.isStage || false,
					authService,
				);
		}

		const initialFeatureServices = {
			localeService,
			loggerService,
			i18nService,
			contentService,
			footnoteReferenceService,
			footnoteService,
			trackingService,
			asyncSSRManager,
			numberFormatterService,
			eCommerceService,
			oneGraphClient,
			authService,
		};

		return {
			render: () => {
				return asyncStateHolder ? (
					<FeatureApp
						featureServices={initialFeatureServices}
						initialState={asyncStateHolder}
						oneGraphClient={oneGraphClient}
					/>
				) : null;
			},
		};
	},
};

export default featureAppDefinition;
